<template>
    <div
        v-if="invoice && !invoiceLoadError"
        class="invoice-container-root bg-base-100"
    >
        <div class="flex-[20%] min-w-[300px]">
            <div class="p-2">
                <div class="py-2" data-cy="invoice-number">Faktura {{ invoiceNo }}</div>
                <sub-account-link class="py-2 text-xl text-primary underline" :uuid="subAccountUuid">
                    {{ name }}
                </sub-account-link>
            </div>

            <div>
                <div class="px-3">
                    <div class="flex">
                        <div class="">Forfallsdato:</div>
                        <div class="mr-0 ml-auto">{{ dueDate }}</div>
                    </div>
                    <div class="flex">
                        <div class="">Fakturadato:</div>
                        <div class="mr-0 ml-auto pb-6">{{ date }}</div>
                    </div>
                    <div class="flex">
                        <div class="">Betalingsstatus:</div>
                        <div class="mr-0 ml-auto">{{ setInvoiceState }}</div>
                    </div>
                    <div class="flex">
                        <div class="">Fakturabeløp:</div>
                        <div class="mr-0 ml-auto">{{ formatCurrency(amount) }}</div>
                    </div>
                    <div class="flex">
                        <div class="">Balanse:</div>
                        <div class="mr-0 ml-auto pb-2" data-cy="invoice-balance-amount">{{ formatCurrency(balance) }}</div>
                    </div>

                    <UButton
                        v-if="balance !== 0"
                        class="mb-4"
                        size="sm"
                        data-cy="pay-invoice-button"
                        @click="createPaymentTransaction"
                    >
                        Registrer betaling
                    </UButton>
                    <UButton
                        v-if="isOutgoingInvoice"
                        class="mb-4 ml-4"
                        color="error"
                        size="sm"
                        data-cy="credit-note-button"
                        :disabled="invoiceBalance?.balance === 0 || invoice?.type !== 'outgoing'"
                        @click="createCreditingInvoice"
                    >
                        Kreditnota
                    </UButton>

                    <div class="mt-4">Bilag</div>
                    <div
                        v-for="t in transactions"
                        :key="t.uuid"
                        class="my-1"
                    >
                        <div class="flex justify-between gap-4">
                            <transaction-link
                                class="text-primary underline"
                                :transaction-uuid="t.uuid"
                            >
                                {{ t.transactionName }}
                            </transaction-link>
                            <div class="truncate">{{ t.transactionLines.find(tl => tl.accountNo === '1500')?.description ?? '' }}</div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="px-3 py-4">
                <div v-if="debtCollectionEnabled" class="flex flex-col gap-2">
                    <div class="text-xl">
                        Inkasso
                    </div>
                    <div class="inline-flex min-w-64 justify-between items-center">
                        <label>Overstyr inkassoinnstillinger</label>
                        <USwitch
                            id="toggle-kravia-settings"
                            v-model="newInvoiceDebtCollectionSettings.invoiceSpecificSettings"
                            class="mx-2"
                            :disabled="isRegisteredInDebtCollector"
                        />
                    </div>
                    <SimpleSelect
                        v-model="selectedFirstReminder"
                        :options="reminderOptions"
                        :disabled="!newInvoiceDebtCollectionSettings.invoiceSpecificSettings || isRegisteredInDebtCollector"
                        placeholder="Velg første påminnelse"
                        label="name"
                        track-by="value"
                    />
                    <div class="inline-flex items-center justify-between gap-2 mt-2">
                        <UInput
                            v-model="newInvoiceDebtCollectionSettings.intervalDays"
                            type="number"
                            :disabled="!newInvoiceDebtCollectionSettings.invoiceSpecificSettings || isRegisteredInDebtCollector"
                            label="Dager før påminnelse"
                            data-cy="reminder-days-input"
                        >
                            <u-input-floating-label>Dager før påminnelse</u-input-floating-label>
                        </UInput>
                        <UButton size="sm" :disabled="isRegisteredInDebtCollector || debtCollectionSettingsUnchanged" @click="updateInvoiceSettings">Oppdater</UButton>
                    </div>
                </div>
                <div v-else>
                    Inkassointegrasjon er ikke aktivert.
                    <targeting-router-link
                        class="text-primary underline underline-offset-2 hover:no-underline ml-2 w-fit"
                        :to="{...ORG_SETTINGS, queryValues: {tab: 'debtCollection'}}"
                    >
                        Aktiver her
                    </targeting-router-link>
                </div>
            </div>
        </div>
        <document-preview-multiple v-if="documentMetasUuids.length > 0" :document-meta-uuids="documentMetasUuids" class="flex-[80%] h-full w-full min-h-[400px]"/>
    </div>
    <div v-else-if="invoiceLoadError" class="flex items-center justify-center w-full h-full">
        Spesifisert faktura finnes ikke i dette foretaket
    </div>
    <div v-else class="flex items-center justify-center w-full h-full">
        <span class="loading loading-spinner loading-lg"/>
    </div>
</template>
<script setup lang="ts">
import {computed, inject, onMounted, onUnmounted, Ref, ref, watch} from 'vue'
import {getDebtCollectionSettings, getExtendedInvoiceInfo, getInvoice, getInvoiceBalance, setDebtCollectionSettings, useInvoiceService} from '@/invoice/invoice.service'
import {CUSTOMER, SubAccount} from '@/subaccount/types/SubAccount'
import {TransactionLine} from '@/types/transaction/TransactionLine'
import {DocumentMeta} from '@/types/transaction/DocumentMeta'
import {getTransactionsByInvoiceUuid} from '@/transaction/transaction.service'
import {Transaction} from '@/types/transaction/Transaction'
import {getTransactionDocuments} from '@/services/infrastructure/storage.service'
import {formatCurrency} from '@/services/formatter.service'
import TransactionLink from '@/transaction/TransactionLink.vue'
import SubAccountLink from '@/subaccount/components/SubAccountLink.vue'
import {findSubAccountByUuid} from '@/subaccount/subAccount.service'
import {CUSTOMER_ACCOUNT_NO, INCOME_ACCOUNT_NO} from '@/bookkeeping-account/accountTypes'
import {useEmitter} from '@/services/infrastructure/emitter'
import {TRANSACTION_CREATED, TRANSACTION_UPDATED} from '@/types/transaction/TransactionEvent'
import {Invoice, InvoiceDebtCollectionSettings, InvoiceExtendedInfo, OutgoingInvoice} from '@/invoice/types/Invoice'
import {ORG_SWITCHED} from '@/types/events'
import {useSetting} from '@/setting/setting.service'
import { TargetingRouterLink } from '@tritt/multipanel'
import {ORG_SETTINGS} from '@/org-settings'
import {KraviaReminder, reminderOptions} from '@/debt-collection/KraviaReminder'
import SimpleSelect from '@/components/SimpleSelect.vue'
import {cloneDeep} from 'lodash'
import UInputFloatingLabel from '@/components/UInputFloatingLabel.vue'
import {DimensionType} from '@/dimensions/Dimension'
import DocumentPreviewMultiple from '@/document/DocumentPreviewMultiple.vue'

// TODO use sub account lines instead of invoice balance
const invoiceBalance = ref()
const invoice = ref<Invoice | OutgoingInvoice>()
const transactions = ref<Array<Transaction>>([])
const documentMetasUuids = ref<string[]>([])
const {setting: kraviaCreditorIdSetting} = useSetting<string>('KRAVIA_CREDITOR_ID')
const extendedInvoiceInfo = ref<InvoiceExtendedInfo>()

const panelId: Ref<number> | undefined = inject('panelId', ref(1))
const {payInvoice, creditInvoice, creditInvoiceV2} = useInvoiceService()
const {onEvents, cleanUp} = useEmitter()

const props = defineProps({
    invoiceUuid: {
        type: String,
        required: true
    }
})


const invoiceLoadError = ref(false)

const invoiceUuid = computed(() => {
    return props.invoiceUuid as string
})

const invoiceNo = computed(() => {
    if (invoice.value) {
        return invoice.value.invoiceNo
    }
    return ''
})

const subAccountUuid = computed(() => {
    if (invoiceBalance.value) {
        return invoiceBalance.value.subAccountUuid
    }
    return ''
})

const isOutgoingInvoice = computed(() => {
    return subAccount.value?.relationType === CUSTOMER
})

const date = computed(() => {
    if (invoiceBalance.value) {
        return invoiceBalance.value.date
    }
    return ''
})

const dueDate = computed(() => {
    if (invoiceBalance.value) {
        return invoiceBalance.value.dueDate
    }
    return ''
})

const amount = computed(() => {
    if (invoiceBalance.value) {
        return invoiceBalance.value.amount
    }
    return ''
})

const balance = computed(() => {
    if (invoiceBalance.value) {
        return invoiceBalance.value.balance
    }
    return ''
})

const name = computed(() => {
    if (subAccount.value) {
        return subAccount.value?.name
    }
    return ''
})

const setInvoiceState = computed(() => {
    if (invoiceBalance.value) {
        if (invoiceBalance.value.balance !== 0) {
            const today = new Date()
            const dueDate = new Date(invoiceBalance.value?.dueDate)

            if (today > dueDate) {
                return 'Forfalt'
            } else {
                return 'Ubetalt'
            }
        }
        return 'Betalt'
    }
    return ''
})

const subAccount = computed<SubAccount | undefined>(() => {
    return findSubAccountByUuid(subAccountUuid.value)
})

const findTransactionLinesForInvoice = async () => {
    const searchParams = {
        query: null,
        subAccountUuid: subAccountUuid.value,
        invoiceUuid: invoiceUuid.value,
        fromDate: '2000-01-01',
        toDate: '2099-12-31'
    }
    const result = await getTransactionsByInvoiceUuid(
        searchParams
    )
    transactions.value = result || []
}

const init = async () => {
    invoice.value = undefined
    invoiceBalance.value = undefined
    try {
        invoiceBalance.value = await getInvoiceBalance(invoiceUuid.value)
        await findTransactionLinesForInvoice()
        invoice.value = await getInvoice(invoiceUuid.value)
        invoiceLoadError.value = false
        await fetchDocumentMetas()
        await fetchInvoiceSettings()
    } catch (error) {
        invoiceLoadError.value = true
        invoice.value = undefined
        invoiceBalance.value = undefined
    }
}

const fetchDocumentMetas = async () => {
    const d = await Promise.all(transactions.value.map((t: Transaction) => getTransactionDocuments(t.uuid)))
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    documentMetasUuids.value = d.flatMap((a: Array<DocumentMeta>) => a).sort((a, _) => a.dimensions.some(d => d.name == 'invoice' && d.type === DimensionType.SYSTEM) ? -1 : 1).map(docMeta => docMeta.uuid)
}

const createPaymentTransaction = () => {
    payInvoice(invoiceBalance.value, panelId.value + 1)
}

const isOutgoing = (invoice: Invoice): invoice is OutgoingInvoice => {
    return invoice.type === 'outgoing'
}

const createCreditingInvoice = () => {
    if (invoice.value && isOutgoing(invoice.value) && invoice.value.invoiceLines.every(l => l.accountNo && l.productUuid)) {
        creditInvoiceV2(invoiceBalance.value, invoice.value.invoiceLines, panelId.value + 1, findCustomerLineUuid(transactions.value[0].transactionLines))
    } else {
        const incomeTransactionLines: TransactionLine[] = []
        transactions.value.forEach(t => {
            incomeTransactionLines.push(...t.transactionLines.filter(l => l.accountNo === INCOME_ACCOUNT_NO))
        })
        creditInvoice(invoiceBalance.value, incomeTransactionLines, panelId.value + 1, findCustomerLineUuid(transactions.value[0].transactionLines))
    }
}

const findCustomerLineUuid = (lines: TransactionLine[]) => {
    return lines.find(l => l.accountNo === CUSTOMER_ACCOUNT_NO)?.uuid
}

const loading = ref(true)
const newInvoiceDebtCollectionSettings = ref<InvoiceDebtCollectionSettings>({
    invoiceUuid: invoiceUuid.value,
    invoiceSpecificSettings: false,
    reminderType: '',
    intervalDays: 14
})
const oldInvoiceDebtCollectionSettings = ref<InvoiceDebtCollectionSettings>({
    invoiceUuid: invoiceUuid.value,
    invoiceSpecificSettings: false,
    reminderType: '',
    intervalDays: 14
})

const isRegisteredInDebtCollector = computed(() => {
    return !!extendedInvoiceInfo?.value?.externalId
})

const selectedFirstReminder = computed({
    get: () => reminderOptions.find(option => option.value === newInvoiceDebtCollectionSettings?.value?.reminderType ?? null),
    set: (value: { name: string, value: KraviaReminder }) => {
        newInvoiceDebtCollectionSettings.value.reminderType = value.value
    }
})

const debtCollectionSettingsUnchanged = computed(() => {
    return JSON.stringify(oldInvoiceDebtCollectionSettings.value) === JSON.stringify(newInvoiceDebtCollectionSettings.value)
})

const debtCollectionEnabled = computed(() => {
    if (!kraviaCreditorIdSetting.value) {
        return false
    }
    return kraviaCreditorIdSetting.value.value !== null
})

const fetchInvoiceSettings = async () => {
    const settings = await getDebtCollectionSettings(invoiceUuid.value)
    if (settings) {
        oldInvoiceDebtCollectionSettings.value = {...settings, invoiceUuid: invoiceUuid.value}
        newInvoiceDebtCollectionSettings.value = {...settings, invoiceUuid: invoiceUuid.value}
    }
    extendedInvoiceInfo.value = await getExtendedInvoiceInfo(invoiceUuid.value)
    loading.value = false
}

const updateInvoiceSettings = async () => {
    await setDebtCollectionSettings(newInvoiceDebtCollectionSettings.value)
    oldInvoiceDebtCollectionSettings.value = cloneDeep(newInvoiceDebtCollectionSettings.value)
}

onMounted(async () => {
        await init()
        onEvents([TRANSACTION_CREATED, TRANSACTION_UPDATED, ORG_SWITCHED], init)
    }
)

onUnmounted(cleanUp)

watch(invoiceUuid, init)

</script>


<style scoped>
@reference "@/index.css";
@reference "tailwindcss";

.invoice-container-root {
    padding: 1rem;
    display: flex;
    height: 100%;
    width: 100%;
    gap: 0.5rem;
    min-height: 800px;
}

@container (max-width: 1199px) {
    .invoice-container-root {
        overflow-y: scroll;
        flex-direction: column;
    }
}

@container (min-width: 1200px) {
    .invoice-container-root {
        flex-direction: row;
    }
}
</style>